<template>
  <Transition name="modal">
    <div
      v-if="showExpirationUi"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="hideWarning"
    >
      <!-- Background overlay with blur effect -->
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"></div>
      
      <!-- Modal Container -->
      <div class="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 scale-100 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
          <!-- Decorative gradient top bar -->
          <div class="h-1.5 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500"></div>
          
          <!-- Header -->
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-start justify-between">
              <div class="flex items-start space-x-4 flex-1">
                <!-- Icon with animated pulse -->
                <div class="flex-shrink-0">
                  <div class="relative">
                    <div class="absolute inset-0 bg-amber-400/20 rounded-full animate-ping"></div>
                    <div class="relative w-14 h-14 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40 rounded-2xl flex items-center justify-center shadow-lg">
                      <svg class="w-7 h-7 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                    </div>
                  </div>
                </div>
                
                <!-- Title and message -->
                <div class="flex-1 min-w-0 pt-1">
                  <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                    {{ warningOptions.title }}
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    {{ warningOptions.message }}
                  </p>
                </div>
              </div>
              
              <!-- Close button -->
              <button 
                @click="hideWarning"
                class="flex-shrink-0 ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                aria-label="Close"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Body Content -->
          <div class="px-6 pb-6">
            <!-- Countdown Timer -->
            <div v-if="countdown !== null && countdown > 0" class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <svg class="w-5 h-5 text-red-600 dark:text-red-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="text-sm font-semibold text-red-700 dark:text-red-300">
                    Auto-logout in:
                  </span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-2xl font-bold text-red-600 dark:text-red-400 tabular-nums">
                    {{ countdown }}
                  </span>
                  <span class="text-sm text-red-600 dark:text-red-400">seconds</span>
                </div>
              </div>
            </div>
            
            <!-- Info box -->
            <div class="mb-6 p-4 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/50 rounded-xl">
              <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p class="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                  <span v-if="countdown !== null && countdown > 0">
                    You will be automatically logged out in {{ countdown }} second{{ countdown > 1 ? 's' : '' }} if no action is taken.
                  </span>
                  <span v-else>
                    Click "Continue" to refresh your session, or "Logout" to end your current session.
                  </span>
                </p>
              </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="flex flex-col sm:flex-row gap-3">
              <button 
                @click="handleLogout"
                class="flex-1 group relative px-5 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-xl transition-all duration-200 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md active:scale-95"
              >
                <span class="relative flex items-center justify-center">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                  </svg>
                  {{ warningOptions.logoutText }}
                </span>
              </button>
              <button 
                @click="handleContinue"
                class="flex-1 group relative px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95"
              >
                <span class="relative flex items-center justify-center">
                  <svg class="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  {{ warningOptions.continueText }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { useTokenExpiration } from '../composables/useTokenExpiration'
import { useAuthStore } from '../stores/auth'
import { isTeacherSession } from '../utils/isTeacherSession'

const { 
  showWarning, 
  warningOptions, 
  countdown,
  hideWarning, 
  handleContinue, 
  handleLogout 
} = useTokenExpiration()

const auth = useAuthStore()

// Both must agree — clearing only localStorage OR only Pinia used to leave the modal visible
const isUserAuthenticated = computed(() => {
  return (
    auth.isAuthenticated && localStorage.getItem('isAuthenticated') === 'true'
  )
})

/** Teachers (OTP): never show session-expiration modal */
const showExpirationUi = computed(
  () => showWarning.value && isUserAuthenticated.value && !isTeacherSession()
)

// CRITICAL: Hide modal if user logs out while modal is visible
watch(() => auth.isAuthenticated, (isAuthenticated) => {
  if (!isAuthenticated && showWarning.value) {
    hideWarning()
  }
}, { immediate: true })

// Watch authentication status and hide modal if user logs out
watch(isUserAuthenticated, (authenticated) => {
  if (!authenticated && showWarning.value) {
    hideWarning()
  }
}, { immediate: true })
</script>

<style scoped>
/* Modal enter/leave animations */
.modal-enter-active {
  transition: opacity 0.3s ease;
}

.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .relative {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.modal-leave-to .relative {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

/* Pulse animation for icon */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>
